<template>
  <div v-if="visivel">
    <!-- Overlay escuro com "buraco" no elemento destacado -->
    <div class="tutorial-overlay" @click="clicouOverlay"></div>

    <!-- Recorte iluminado sobre o elemento alvo -->
    <div
      v-if="retanguloAlvo"
      class="tutorial-spotlight"
      :style="{
        top:    retanguloAlvo.top    + 'px',
        left:   retanguloAlvo.left   + 'px',
        width:  retanguloAlvo.width  + 'px',
        height: retanguloAlvo.height + 'px',
      }"
    ></div>

    <!-- Tooltip posicionado dinamicamente -->
    <div
      class="tutorial-tooltip"
      :class="'tutorial-tooltip--' + posicaoTooltip"
      :style="estiloTooltip"
    >
      <!-- Cabeçalho -->
      <div class="tutorial-tooltip__header">
        <span class="tutorial-tooltip__icone">💡</span>
        <span class="tutorial-tooltip__titulo">{{ passoAtual.nm_titulo }}</span>
        <span class="tutorial-tooltip__contador">{{ indiceGlobal }} / {{ totalGlobal }}</span>
      </div>

      <!-- Corpo -->
      <div class="tutorial-tooltip__corpo">
        <p>{{ passoAtual.ds_descricao }}</p>
        <img
          v-if="passoAtual.ds_imagem_url"
          :src="passoAtual.ds_imagem_url"
          class="tutorial-tooltip__imagem"
          alt="Ilustração do passo"
        />
      </div>

      <!-- Barra de progresso -->
      <div class="tutorial-tooltip__progresso">
        <div
          class="tutorial-tooltip__progresso-barra"
          :style="{ width: (indiceGlobal / totalGlobal * 100) + '%' }"
        ></div>
      </div>

      <!-- Rodapé -->
      <div class="tutorial-tooltip__rodape">
        <b-button size="sm" variant="outline-secondary" @click="pular">
          Pular tutorial
        </b-button>
        <div class="tutorial-tooltip__navegacao">
          <b-button
            size="sm"
            variant="outline-primary"
            @click="anterior"
            :disabled="indicePasso === 0"
          >
            ← Anterior
          </b-button>
          <b-button
            v-if="indicePasso < passosNaRota.length - 1"
            size="sm"
            variant="primary"
            @click="proximo"
          >
            Próximo →
          </b-button>
          <b-button
            v-else-if="indiceGlobal < totalGlobal"
            size="sm"
            variant="primary"
            @click="proximo"
          >
            Próximo → <small style="opacity:0.75;font-size:10px">(próx. página)</small>
          </b-button>
          <b-button
            v-else
            size="sm"
            variant="success"
            @click="concluir"
          >
            ✓ Concluir
          </b-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import API from '@/services/api';

export default {
  name: 'TutorialGuiado',

  props: {
    /**
     * Nome do tutorial — chave usada nos endpoints do backend.
     * Deve corresponder ao campo nm_tutorial da tabela tb_tutorial no banco.
     * Ex: 'pedido_retirada'
     */
    nmTutorial: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      visivel:           false,
      indicePasso:       0,      // índice dentro de passosNaRota (passos desta página)
      passosNaRota:      [],     // subconjunto de todos os passos que pertencem à rota atual
      todosPasosOrdem:   [],     // todos os passos do tutorial, ordenados por nr_ordem
      retanguloAlvo:     null,
      posicaoTooltip:    'bottom',
      estiloTooltip:     {},
    };
  },

  computed: {
    passoAtual() {
      return this.passosNaRota[this.indicePasso] || {};
    },
    // Número global do passo atual (ex: "3 / 10") para mostrar ao usuário
    indiceGlobal() {
      if (!this.passoAtual || !this.passoAtual.nr_ordem) return this.indicePasso + 1;
      return this.todosPasosOrdem.findIndex(function(p) {
        return p.nr_ordem === this.passoAtual.nr_ordem;
      }.bind(this)) + 1;
    },
    totalGlobal() {
      return this.todosPasosOrdem.length || this.passosNaRota.length;
    },
  },

  methods: {
    // -------------------------------------------------------------------------
    // Chave de progresso no localStorage — por usuário e por tutorial
    // -------------------------------------------------------------------------
    _chaveStorage() {
      var usr = localStorage.getItem('usrEmail') || 'anon';
      return 'tutorial_' + this.nmTutorial + '_' + usr;
    },

    _salvarProgresso(nrOrdem) {
      localStorage.setItem(this._chaveStorage(), String(nrOrdem));
    },

    _lerProgresso() {
      return parseInt(localStorage.getItem(this._chaveStorage()) || '0', 10);
    },

    _limparProgresso() {
      localStorage.removeItem(this._chaveStorage());
    },

    // -------------------------------------------------------------------------
    // Rota atual — usa window.location.pathname para compatibilidade máxima
    // -------------------------------------------------------------------------
    _rotaAtual() {
      return window.location.pathname.replace(/\/$/, '') || '/';
    },

    // -------------------------------------------------------------------------
    // iniciar() — ponto de entrada chamado pelo mounted() de cada página
    //
    // O backend retorna:
    // {
    //   data: {
    //     ic_exibir_tutorial: true|false,
    //     passos: [
    //       {
    //         nr_ordem:       1,
    //         nm_titulo:      "...",
    //         ds_descricao:   "...",
    //         ds_seletor_css: "#meu-elemento",  // null = tooltip centralizado
    //         nm_posicao:     "bottom",
    //         ds_imagem_url:  null,
    //         nm_rota:        "/painel"          // rota onde este passo deve aparecer
    //       },
    //       ...
    //     ]
    //   }
    // }
    // -------------------------------------------------------------------------
    async iniciar() {
      try {
        var res = await API.get('usuario/tutorial/' + this.nmTutorial);
        var data = res.data && res.data.data;
        if (!data || !data.ic_exibir_tutorial) return;

        this.todosPasosOrdem = (data.passos || []).slice().sort(function(a, b) {
          return (a.nr_ordem || 0) - (b.nr_ordem || 0);
        });

        if (this.todosPasosOrdem.length === 0) return;

        this._mostrarPassosDaRota();
      } catch (e) {
        console.warn('Tutorial: não foi possível consultar status.', e);
      }
    },

    // -------------------------------------------------------------------------
    // Filtra os passos da rota atual e retoma do progresso salvo
    // -------------------------------------------------------------------------
    _mostrarPassosDaRota() {
      var rota = this._rotaAtual();
      var progressoOrdem = this._lerProgresso();

      // Passos que pertencem à rota atual
      var passosRota = this.todosPasosOrdem.filter(function(p) {
        if (!p.nm_rota) return true; // sem rota definida = aparece em qualquer página
        return p.nm_rota === rota;
      });

      if (passosRota.length === 0) return; // nenhum passo para esta rota

      this.passosNaRota = passosRota;

      // Retoma do progresso salvo: acha o índice dentro dos passosNaRota
      // cujo nr_ordem >= progressoOrdem (retoma de onde parou)
      var indiceRetomada = 0;
      if (progressoOrdem > 0) {
        for (var i = 0; i < passosRota.length; i++) {
          if ((passosRota[i].nr_ordem || 0) >= progressoOrdem) {
            indiceRetomada = i;
            break;
          }
        }
      }

      this.indicePasso = indiceRetomada;
      this.visivel = true;
      this.$emit('ativo', true);
      // Dois nextTick: primeiro para o pai re-computar dados que dependem de @ativo
      // (ex: linha mockada do tutorial), segundo para o Vue renderizar o novo DOM
      this.$nextTick(function() {
        this.$nextTick(function() { this.posicionarTooltip(); }.bind(this));
      }.bind(this));
    },

    /**
     * Força a exibição do tutorial independente do backend.
     * Útil para testes. Uso: this.$refs.tutorial.forcarExibicao()
     */
    forcarExibicao() {
      var rota = this._rotaAtual();
      var passosRota = this.todosPasosOrdem.filter(function(p) {
        return !p.nm_rota || p.nm_rota === rota;
      });

      if (passosRota.length === 0) {
        console.warn('Tutorial: nenhum passo para a rota "' + rota + '". Verifique nm_rota no BD.');
        return;
      }

      this.passosNaRota = passosRota;
      this.indicePasso  = 0;
      this.visivel      = true;
      this._limparProgresso();
      this.$nextTick(function() { this.posicionarTooltip(); }.bind(this));
    },

    posicionarTooltip() {
      var seletor = this.passoAtual.ds_seletor_css;
      if (!seletor) {
        // Sem seletor: tooltip centralizado na tela (passo informativo)
        this._fecharMenuAberto();
        this.retanguloAlvo  = null;
        this.estiloTooltip  = { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
        this.posicaoTooltip = 'centro';
        return;
      }

      // Se há uma ação "antes" (ex: abrir submenu clicando no pai), executa primeiro
      var self = this;
      var acaoAntes = this.passoAtual.ds_acao_antes || null;
      var delayInicio = 0;

      console.log('Tutorial passo', this.passoAtual.nr_ordem, '| ds_acao_antes:', acaoAntes, '| ds_seletor_css:', seletor);

      if (acaoAntes) {
        // Suporte a múltiplas ações separadas por "|"
        // ex: "fill:#nrPedido:1|click:.btn-buscar"
        var acoes = acaoAntes.split('|');
        for (var a = 0; a < acoes.length; a++) {
          var partes   = acoes[a].split(':');   // ex: "click:.meu-seletor"  ou  "hover:.meu-seletor"  ou  "select:#id:valor"  ou  "fill:#id:valor"
          var tipoAcao = partes[0];
          // Para "select" e "fill", o formato é "tipo:seletor:valor" — partes[1] é o seletor, partes[2] é o valor
          // Para "hover" e "click", o seletor pode conter ":" (ex: atributo CSS) — usamos slice(1).join(":")
          var selPai   = (tipoAcao === 'select' || tipoAcao === 'fill') ? partes[1] : partes.slice(1).join(':');

          // bv-modal não precisa de elemento DOM — abre diretamente pelo $bvModal
          if (tipoAcao === 'bv-modal') {
            console.log('Tutorial: abrindo modal Bootstrap-Vue:', selPai);
            this.$bvModal.show(selPai);
            continue;
          }

          var elPai    = null;
          try {
            elPai = document.querySelector(selPai);
          } catch (e) {
            console.warn('Tutorial: seletor de ds_acao_antes inválido:', selPai, e.message);
          }
          if (elPai) {
            console.log('Tutorial: executando ação "' + tipoAcao + '" em:', selPai, elPai);
            if (tipoAcao === 'hover') {
              // Adiciona classe que força o submenu a abrir (simula :hover via JS)
              elPai.classList.add('tutorial-forcar-hover');
              this._elComHoverForcado = elPai;
            } else if (tipoAcao === 'click') {
              console.log('Tutorial: disparando .click() em:', selPai, elPai);
              elPai.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
            } else if (tipoAcao === 'select') {
              // Formato: "select:seletor:valor"  — seleciona uma opção em um <select>
              var valorSelect = partes[2] !== undefined ? partes[2] : '';
              elPai.value = valorSelect;
              // Dispara evento change para frameworks reativos (Vue) detectarem a mudança
              elPai.dispatchEvent(new Event('change', { bubbles: true }));
            } else if (tipoAcao === 'fill') {
              // Formato: "fill:seletor:valor"  — preenche um <input> e notifica o Vue
              var valorFill = partes[2] !== undefined ? partes[2] : '';
              // Usa o setter nativo para o Vue 2 (input-binding) detectar a mudança
              var nativeInputSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value');
              if (nativeInputSetter) nativeInputSetter.set.call(elPai, valorFill);
              else elPai.value = valorFill;
              elPai.dispatchEvent(new Event('input',  { bubbles: true }));
              elPai.dispatchEvent(new Event('change', { bubbles: true }));
            }
          } else {
            console.warn('Tutorial: elemento de ds_acao_antes não encontrado:', selPai);
          }
        }
        // Delay maior para ações que disparam requisições (click em buscar)
        // bv-modal e click: 800ms para aguardar a animação do modal abrir
        var temClick   = acoes.some(function(a) { return a.split(':')[0] === 'click'; });
        var temBvModal = acoes.some(function(a) { return a.split(':')[0] === 'bv-modal'; });
        delayInicio = (temClick || temBvModal) ? 800 : 600;
      } else {
        // Nenhuma ação antes: fecha qualquer submenu que tenha sido aberto
        this._fecharMenuAberto();
      }

      setTimeout(function() {
        var el = seletor ? document.querySelector(seletor) : null;
        if (seletor && !el) {
          console.warn('Tutorial: elemento não encontrado para seletor:', seletor, '— exibindo tooltip centralizado');
          // Elemento não encontrado (ex: tabela vazia) — exibe o tooltip no centro da tela
          // sem spotlight, mas mantendo o conteúdo do passo atual
          self.retanguloAlvo  = null;
          self.posicaoTooltip = 'centro';
          self.estiloTooltip  = { top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '320px' };
          return;
        }

        console.log('Tutorial: elemento encontrado:', seletor, el);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });

        setTimeout(function() {
          // Passo sem seletor (centralizado) — não precisa de rect
          if (!el) {
            self.retanguloAlvo  = null;
            self.posicaoTooltip = 'centro';
            self.estiloTooltip  = { top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '320px' };
            return;
          }

          var rect = el.getBoundingClientRect();

          // Spotlight usa position:fixed — coordenadas relativas ao viewport (sem scrollY)
          self.retanguloAlvo = {
            top:    rect.top    - 6,
            left:   rect.left   - 6,
            width:  rect.width  + 12,
            height: rect.height + 12,
          };

          var posicao = self.passoAtual.nm_posicao || 'bottom';
          self.posicaoTooltip = posicao;

          var tooltipLargura = 320;
          var tooltipTop, tooltipLeft;
          var margem = 16;

          // Estima altura do tooltip para evitar que saia da viewport.
          // Usamos o elemento real se já estiver renderizado, caso contrário estimamos.
          var tooltipEl = document.querySelector('.tutorial-tooltip');
          var tooltipAltura = tooltipEl ? tooltipEl.offsetHeight : 220;

          if (posicao === 'bottom') {
            tooltipTop  = rect.bottom + margem;
            tooltipLeft = rect.left + (rect.width / 2) - (tooltipLargura / 2);
          } else if (posicao === 'top') {
            tooltipTop  = rect.top - margem - tooltipAltura;
            tooltipLeft = rect.left + (rect.width / 2) - (tooltipLargura / 2);
          } else if (posicao === 'right') {
            tooltipTop  = rect.top + (rect.height / 2) - (tooltipAltura / 2);
            tooltipLeft = rect.right + margem;
          } else if (posicao === 'left') {
            tooltipTop  = rect.top + (rect.height / 2) - (tooltipAltura / 2);
            tooltipLeft = rect.left - tooltipLargura - margem;
          }

          var maxLeft   = window.innerWidth  - tooltipLargura - 8;
          var maxTop    = window.innerHeight - tooltipAltura  - 8;
          tooltipLeft = Math.max(8, Math.min(tooltipLeft, maxLeft));
          tooltipTop  = Math.max(8, Math.min(tooltipTop,  maxTop));

          self.estiloTooltip = {
            top:   tooltipTop  + 'px',
            left:  tooltipLeft + 'px',
            width: tooltipLargura + 'px',
          };

          // Reajusta após o Vue re-renderizar o tooltip com o novo conteúdo
          // (a altura real pode ser diferente da altura do passo anterior)
          self.$nextTick(function() {
            var tooltipElReal = document.querySelector('.tutorial-tooltip');
            if (!tooltipElReal) return;
            var alturaReal = tooltipElReal.offsetHeight;
            var tTop = parseFloat(self.estiloTooltip.top);
            var maxTopReal = window.innerHeight - alturaReal - 8;
            if (tTop > maxTopReal) {
              self.estiloTooltip = Object.assign({}, self.estiloTooltip, {
                top: Math.max(8, maxTopReal) + 'px',
              });
            }
          });
        }, 300);
      }, delayInicio);
    },

    // Fecha qualquer submenu que tenha sido aberto pelo tutorial
    _fecharMenuAberto() {
      if (this._elComHoverForcado) {
        this._elComHoverForcado.classList.remove('tutorial-forcar-hover');
        this._elComHoverForcado = null;
      }
    },

    proximo() {
      // Salva o progresso do passo atual antes de avançar
      this._salvarProgresso(this.passoAtual.nr_ordem || 0);

      if (this.indicePasso < this.passosNaRota.length - 1) {
        // Ainda há passos na rota atual — avança
        this.indicePasso++;
        // Duplo nextTick: garante que o pai re-renderiza props para componentes filhos
        // (ex: linha mock passada via :resultado="itemFiltrado" → filho renderiza → DOM pronto)
        this.$nextTick(function() {
          this.$nextTick(function() { this.posicionarTooltip(); }.bind(this));
        }.bind(this));
      } else {
        // Acabaram os passos desta rota — navega para a próxima rota do tutorial
        this._fecharMenuAberto();
        var proximaRota = this._proximaRota();
        this.visivel = false;
        this.$emit('ativo', false);
        if (proximaRota) {
          window.location.href = proximaRota;
        }
      }
    },

    // Retorna o nm_rota do primeiro passo global que seja de uma rota diferente da atual
    _proximaRota() {
      var ordemAtual = this.passoAtual ? (this.passoAtual.nr_ordem || 0) : 0;
      var rotaAtual  = this._rotaAtual();
      for (var i = 0; i < this.todosPasosOrdem.length; i++) {
        var p = this.todosPasosOrdem[i];
        if ((p.nr_ordem || 0) > ordemAtual && p.nm_rota && p.nm_rota !== rotaAtual) {
          return p.nm_rota;
        }
      }
      return null;
    },

    anterior() {
      if (this.indicePasso > 0) {
        this.indicePasso--;
        this._salvarProgresso(this.passosNaRota[this.indicePasso].nr_ordem || 0);
        this.$nextTick(function() {
          this.$nextTick(function() { this.posicionarTooltip(); }.bind(this));
        }.bind(this));
      }
    },

    async concluir() {
      this.visivel = false;
      this.$emit('ativo', false);
      this._limparProgresso();
      await this.registrar(1);
      this.$emit('concluido');
    },

    async pular() {
      this.visivel = false;
      this.$emit('ativo', false);
      this._limparProgresso();
      this._fecharMenuAberto();
      await this.registrar(3);
      this.$emit('pulado');
    },

    // Clique no overlay = "mais tarde" (retoma na próxima visita, do passo atual)
    async clicouOverlay() {
      this.visivel = false;
      this.$emit('ativo', false);
      this._fecharMenuAberto();
      await this.registrar(2);
      this.$emit('mais-tarde');
    },

    async registrar(icStatus) {
      try {
        await API.post('usuario/tutorial/registrar', {
          nm_tutorial: this.nmTutorial,
          ic_status:   icStatus,
        });
      } catch (e) {
        console.warn('Tutorial: não foi possível registrar status.', e);
      }
    },
  },
};
</script>

<style scoped>
/* Overlay escuro */
.tutorial-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 9000;
  cursor: pointer;
}

/* Recorte luminoso (fica acima do overlay, abaixo do tooltip) */
.tutorial-spotlight {
  position: fixed;
  z-index: 9001;
  border-radius: 6px;
  box-shadow:
    0 0 0 4px #ffffff,
    0 0 0 9999px rgba(0, 0, 0, 0.55);
  pointer-events: none;
  transition: all 0.3s ease;
}

/* Tooltip */
.tutorial-tooltip {
  position: fixed;
  z-index: 9002;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.22);
  padding: 0;
  overflow: hidden;
  transition: all 0.25s ease;
}

/* Seta do tooltip (pseudo-elemento) */
.tutorial-tooltip--bottom::before {
  content: '';
  position: absolute;
  top: -8px;
  left: 50%;
  transform: translateX(-50%);
  border-left: 9px solid transparent;
  border-right: 9px solid transparent;
  border-bottom: 9px solid #fff;
  filter: drop-shadow(0 -2px 2px rgba(0,0,0,0.08));
}
.tutorial-tooltip--top::after {
  content: '';
  position: absolute;
  bottom: -8px;
  left: 50%;
  transform: translateX(-50%);
  border-left: 9px solid transparent;
  border-right: 9px solid transparent;
  border-top: 9px solid #fff;
}
.tutorial-tooltip--right::before {
  content: '';
  position: absolute;
  left: -8px;
  top: 50%;
  transform: translateY(-50%);
  border-top: 9px solid transparent;
  border-bottom: 9px solid transparent;
  border-right: 9px solid #fff;
}
.tutorial-tooltip--left::before {
  content: '';
  position: absolute;
  right: -8px;
  top: 50%;
  transform: translateY(-50%);
  border-top: 9px solid transparent;
  border-bottom: 9px solid transparent;
  border-left: 9px solid #fff;
}

.tutorial-tooltip__header {
  display: flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #004e86, #1a7abf);
  color: #fff;
  padding: 12px 16px;
}

.tutorial-tooltip__icone {
  font-size: 18px;
}

.tutorial-tooltip__titulo {
  flex: 1;
  font: normal normal bold 14px/1.3 'Kanit', sans-serif;
}

.tutorial-tooltip__contador {
  font-size: 12px;
  opacity: 0.8;
  white-space: nowrap;
}

.tutorial-tooltip__corpo {
  padding: 14px 16px 8px;
  font: normal normal 400 13px/1.5 'Kanit', sans-serif;
  color: #333;
}

.tutorial-tooltip__corpo p {
  margin: 0;
}

.tutorial-tooltip__imagem {
  margin-top: 10px;
  width: 100%;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
}

.tutorial-tooltip__progresso {
  height: 3px;
  background: #e9ecef;
  margin: 8px 16px;
  border-radius: 2px;
  overflow: hidden;
}

.tutorial-tooltip__progresso-barra {
  height: 100%;
  background: linear-gradient(90deg, #004e86, #1a7abf);
  border-radius: 2px;
  transition: width 0.3s ease;
}

.tutorial-tooltip__rodape {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px 14px;
  gap: 8px;
}

.tutorial-tooltip__navegacao {
  display: flex;
  gap: 6px;
}
</style>

<!-- Estilo global (sem scoped): afeta o Menu.vue para abrir submenu via tutorial -->
<style>
.arealogada-card-element.tutorial-forcar-hover > .arealogada-subitem-container {
  display: flex !important;
}
</style>
